'use strict';

const algoliaClient = algoliasearch('0RU61W5XPV', '880d477c61e0dfd5b9f7b136c3d4969f');
const isProd = window.location.hostname.includes('developers.soracom.io');
const indexName = isProd ? 'prod_developers_search' : 'stg_developers_search';

function loadAssetManifest() {
  if (window.SoracomAssets && typeof window.SoracomAssets.loadManifest === 'function') {
    return window.SoracomAssets.loadManifest();
  }
  return Promise.resolve({});
}

function resolveAssetPath(relativePath) {
  if (window.SoracomAssets && typeof window.SoracomAssets.resolveAssetPath === 'function') {
    return window.SoracomAssets.resolveAssetPath(relativePath);
  }
  const normalized = String(relativePath || '').replace(/^\/+/, '');
  return `/${normalized}`;
}

function resolveThemeImagePath(iconName) {
  const base = String(window.THEME_IMAGE_BASE || '').replace(/\/+$/, '');
  const safeName = String(iconName || 'documents').replace(/[^a-zA-Z0-9._-]/g, '');
  if (!base) return '';
  return `${base}/${safeName}.svg`;
}

function resolveFingerprintedIconPath(iconName) {
  const safeName = String(iconName || 'documents').replace(/[^a-zA-Z0-9._-]/g, '');
  return resolveAssetPath(`assets/images/${safeName}.svg`);
}

// Resolve icon paths in a way that works in:
// - Grav dev (served under /user/themes/...): THEME_IMAGE_BASE points at theme images
// - Blackhole static output (S3): THEME_IMAGE_BASE should point at a valid images base
// - Optional hashed assets: fingerprints.json resolves assets/images/<icon>.svg -> assets/images/<icon>.<hash>.svg
function resolveSearchIconPath(iconName) {
  // - Dev (Grav): THEME_IMAGE_BASE points at /user/themes/... and those files exist.
  // - Static (Blackhole/S3): assets are served under /assets/... and may be fingerprinted/hashed.
  const base = String(window.THEME_IMAGE_BASE || '');
  const isThemeDev = base.indexOf('/user/themes/') !== -1;
  return isThemeDev ? resolveThemeImagePath(iconName) : resolveFingerprintedIconPath(iconName);
}

// If an icon is missing, swap to a safe fallback without rendering a broken image icon.
// Note: Algolia's `html` templating treats `onerror=` as an event handler (function), so we
// cannot use an inline string handler. Use a DOM error listener instead.
(function setupAutocompleteIconFallback() {
  try {
    const fallbackSrc = resolveSearchIconPath('documents');
    document.addEventListener('error', function (e) {
      const img = e && e.target;
      if (!img || img.tagName !== 'IMG') return;
      if (!img.classList || !img.classList.contains('aa-ItemIconLeft')) return;
      const iconName = (img.dataset && img.dataset.icon) ? img.dataset.icon : '';
      const currentSrc = img.currentSrc || img.src || '';
      const stage = img.dataset && img.dataset.fallbackStage ? parseInt(img.dataset.fallbackStage, 10) : 0;

      // Stage 0: try fingerprinted /assets/images (handles hashed filenames)
      if (stage <= 0) {
        const fp = resolveFingerprintedIconPath(iconName);
        if (fp && fp !== currentSrc) {
          if (img.dataset) img.dataset.fallbackStage = '1';
          img.src = fp;
          return;
        }
      }

      // Stage 1: fall back to documents (theme base first, else fingerprinted)
      if (stage <= 1) {
        if (img.dataset) img.dataset.fallbackStage = '2';
        img.src = fallbackSrc;
        return;
      }

      // Stage 2+: give up and hide the broken icon (avoid broken-image placeholder)
      if (img.dataset) img.dataset.fallbackStage = '3';
      img.style.display = 'none';
    }, true);
  } catch (e) {
    // ignore
  }
}());

// SVG Source: https://instantsearchjs.netlify.app/stories/js/?path=/story/metadata-poweredby--default
const renderAlgoliaLogo = (html) => {
  return html`
    <div class="aa-PoweredBy">
        <svg viewBox="0 0 572 64" style="width: auto;"><path class="aa-PoweredBy-text" fill="#36395A" d="M16 48.3c-3.4 0-6.3-.6-8.7-1.7A12.4 12.4 0 0 1 1.9 42C.6 40 0 38 0 35.4h6.5a6.7 6.7 0 0 0 3.9 6c1.4.7 3.3 1.1 5.6 1.1 2.2 0 4-.3 5.4-1a7 7 0 0 0 3-2.4 6 6 0 0 0 1-3.4c0-1.5-.6-2.8-1.9-3.7-1.3-1-3.3-1.6-5.9-1.8l-4-.4c-3.7-.3-6.6-1.4-8.8-3.4a10 10 0 0 1-3.3-7.9c0-2.4.6-4.6 1.8-6.4a12 12 0 0 1 5-4.3c2.2-1 4.7-1.6 7.5-1.6s5.5.5 7.6 1.6a12 12 0 0 1 5 4.4c1.2 1.8 1.8 4 1.8 6.7h-6.5a6.4 6.4 0 0 0-3.5-5.9c-1-.6-2.6-1-4.4-1s-3.2.3-4.4 1c-1.1.6-2 1.4-2.6 2.4-.5 1-.8 2-.8 3.1a5 5 0 0 0 1.5 3.6c1 1 2.6 1.7 4.7 1.9l4 .3c2.8.2 5.2.8 7.2 1.8 2.1 1 3.7 2.2 4.9 3.8a9.7 9.7 0 0 1 1.7 5.8c0 2.5-.7 4.7-2 6.6a13 13 0 0 1-5.6 4.4c-2.4 1-5.2 1.6-8.4 1.6Zm35.6 0c-2.6 0-4.8-.4-6.7-1.3a13 13 0 0 1-4.7-3.5 17.1 17.1 0 0 1-3.6-10.4v-1c0-2 .3-3.8 1-5.6a13 13 0 0 1 7.3-8.3 15 15 0 0 1 6.3-1.4A13.2 13.2 0 0 1 64 24.3c1 2.2 1.6 4.6 1.6 7.2V34H39.4v-4.3h21.8l-1.8 2.2c0-2-.3-3.7-.9-5.1a7.3 7.3 0 0 0-2.7-3.4c-1.2-.7-2.7-1.1-4.6-1.1s-3.4.4-4.7 1.3a8 8 0 0 0-2.9 3.6c-.6 1.5-.9 3.3-.9 5.4 0 2 .3 3.7 1 5.3a7.9 7.9 0 0 0 2.8 3.7c1.3.8 3 1.3 5 1.3s3.8-.5 5.1-1.3c1.3-1 2.1-2 2.4-3.2h6a11.8 11.8 0 0 1-7 8.7 16 16 0 0 1-6.4 1.2ZM80 48c-2.2 0-4-.3-5.7-1a8.4 8.4 0 0 1-3.7-3.3 9.7 9.7 0 0 1-1.3-5.2c0-2 .5-3.8 1.5-5.2a9 9 0 0 1 4.3-3.1c1.8-.7 4-1 6.7-1H89v4.1h-7.5c-2 0-3.4.5-4.4 1.4-1 1-1.6 2.1-1.6 3.6s.5 2.7 1.6 3.6c1 1 2.5 1.4 4.4 1.4 1.1 0 2.2-.2 3.2-.7 1-.4 1.9-1 2.6-2 .6-1 1-2.4 1-4.2l1.7 2.1c-.2 2-.7 3.8-1.5 5.2a9 9 0 0 1-3.4 3.3 12 12 0 0 1-5.3 1Zm9.5-.7v-8.8h-1v-10c0-1.8-.5-3.2-1.4-4.1-1-1-2.4-1.4-4.2-1.4a142.9 142.9 0 0 0-10.2.4v-5.6a74.8 74.8 0 0 1 8.6-.4c3 0 5.5.4 7.5 1.2s3.4 2 4.4 3.6c1 1.7 1.4 4 1.4 6.7v18.4h-5Zm12.9 0V17.8h5v12.3h-.2c0-4.2 1-7.4 2.8-9.5a11 11 0 0 1 8.3-3.1h1v5.6h-2a9 9 0 0 0-6.3 2.2c-1.5 1.5-2.2 3.6-2.2 6.4v15.6h-6.4Zm34.4 1a15 15 0 0 1-6.6-1.3c-1.9-.9-3.4-2-4.7-3.5a15.5 15.5 0 0 1-2.7-5c-.6-1.7-1-3.6-1-5.4v-1c0-2 .4-3.8 1-5.6a15 15 0 0 1 2.8-4.9c1.3-1.5 2.8-2.6 4.6-3.5a16.4 16.4 0 0 1 13.3.2c2 1 3.5 2.3 4.8 4a12 12 0 0 1 2 6H144c-.2-1.6-1-3-2.2-4.1a7.5 7.5 0 0 0-5.2-1.7 8 8 0 0 0-4.7 1.3 8 8 0 0 0-2.8 3.6 13.8 13.8 0 0 0 0 10.3c.6 1.5 1.5 2.7 2.8 3.6s2.8 1.3 4.8 1.3c1.5 0 2.7-.2 3.8-.8a7 7 0 0 0 2.6-2c.7-1 1-2 1.2-3.2h6.2a11 11 0 0 1-2 6.2 15.1 15.1 0 0 1-11.8 5.5Zm19.7-1v-40h6.4V31h-1.3c0-3 .4-5.5 1.1-7.6a9.7 9.7 0 0 1 3.5-4.8A9.9 9.9 0 0 1 172 17h.3c3.5 0 6 1.1 7.9 3.5 1.7 2.3 2.6 5.7 2.6 10v16.8h-6.4V29.6c0-2.1-.6-3.8-1.8-5a6.4 6.4 0 0 0-4.8-1.8c-2 0-3.7.7-5 2a7.8 7.8 0 0 0-1.9 5.5v17h-6.4Zm63.8 1a12.2 12.2 0 0 1-10.9-6.2 19 19 0 0 1-1.8-7.3h1.4v12.5h-5.1v-40h6.4v19.8l-2 3.5c.2-3.1.8-5.7 1.9-7.7a11 11 0 0 1 4.4-4.5c1.8-1 3.9-1.5 6.1-1.5a13.4 13.4 0 0 1 12.8 9.1c.7 1.9 1 3.8 1 6v1c0 2.2-.3 4.1-1 6a13.6 13.6 0 0 1-13.2 9.4Zm-1.2-5.5a8.4 8.4 0 0 0 7.9-5c.7-1.5 1.1-3.3 1.1-5.3s-.4-3.8-1.1-5.3a8.7 8.7 0 0 0-3.2-3.6 9.6 9.6 0 0 0-9.2-.2 8.5 8.5 0 0 0-3.3 3.2c-.8 1.4-1.3 3-1.3 5v2.3a9 9 0 0 0 1.3 4.8 9 9 0 0 0 3.4 3c1.4.7 2.8 1 4.4 1Zm27.3 3.9-10-28.9h6.5l9.5 28.9h-6Zm-7.5 12.2v-5.7h4.9c1 0 2-.1 2.9-.4a4 4 0 0 0 2-1.4c.4-.7.9-1.6 1.2-2.7l8.6-30.9h6.2l-9.3 32.4a14 14 0 0 1-2.5 5 8.9 8.9 0 0 1-4 2.8c-1.5.6-3.4.9-5.6.9h-4.4Zm9-12.2v-5.2h6.4v5.2H248Z"></path><path class="aa-PoweredBy-logo" fill="#003DFF" d="M534.4 9.1H528a.8.8 0 0 1-.7-.7V1.8c0-.4.2-.7.6-.8l6.5-1c.4 0 .8.2.9.6v7.8c0 .4-.4.7-.8.7zM428 35.2V.8c0-.5-.3-.8-.7-.8h-.2l-6.4 1c-.4 0-.7.4-.7.8v35c0 1.6 0 11.8 12.3 12.2.5 0 .8-.4.8-.8V43c0-.4-.3-.7-.6-.8-4.5-.5-4.5-6-4.5-7zm106.5-21.8H528c-.4 0-.7.4-.7.8v34c0 .4.3.8.7.8h6.5c.4 0 .8-.4.8-.8v-34c0-.5-.4-.8-.8-.8zm-17.7 21.8V.8c0-.5-.3-.8-.8-.8l-6.5 1c-.4 0-.7.4-.7.8v35c0 1.6 0 11.8 12.3 12.2.4 0 .8-.4.8-.8V43c0-.4-.3-.7-.7-.8-4.4-.5-4.4-6-4.4-7zm-22.2-20.6a16.5 16.5 0 0 1 8.6 9.3c.8 2.2 1.3 4.8 1.3 7.5a19.4 19.4 0 0 1-4.6 12.6 14.8 14.8 0 0 1-5.2 3.6c-2 .9-5.2 1.4-6.8 1.4a21 21 0 0 1-6.7-1.4 15.4 15.4 0 0 1-8.6-9.3 21.3 21.3 0 0 1 0-14.4 15.2 15.2 0 0 1 8.6-9.3c2-.8 4.3-1.2 6.7-1.2s4.6.4 6.7 1.2zm-6.7 27.6c2.7 0 4.7-1 6.2-3s2.2-4.3 2.2-7.8-.7-6.3-2.2-8.3-3.5-3-6.2-3-4.7 1-6.1 3c-1.5 2-2.2 4.8-2.2 8.3s.7 5.8 2.2 7.8 3.5 3 6.2 3zm-88.8-28.8c-6.2 0-11.7 3.3-14.8 8.2a18.6 18.6 0 0 0 4.8 25.2c1.8 1.2 4 1.8 6.2 1.7s.1 0 .1 0h.9c4.2-.7 8-4 9.1-8.1v7.4c0 .4.3.7.8.7h6.4a.7.7 0 0 0 .7-.7V14.2c0-.5-.3-.8-.7-.8h-13.5zm6.3 26.5a9.8 9.8 0 0 1-5.7 2h-.5a10 10 0 0 1-9.2-14c1.4-3.7 5-6.3 9-6.3h6.4v18.3zm152.3-26.5h13.5c.5 0 .8.3.8.7v33.7c0 .4-.3.7-.8.7h-6.4a.7.7 0 0 1-.8-.7v-7.4c-1.2 4-4.8 7.4-9 8h-.1a4.2 4.2 0 0 1-.5.1h-.9a10.3 10.3 0 0 1-7-2.6c-4-3.3-6.5-8.4-6.5-14.2 0-3.7 1-7.2 3-10 3-5 8.5-8.3 14.7-8.3zm.6 28.4c2.2-.1 4.2-.6 5.7-2V21.7h-6.3a9.8 9.8 0 0 0-9 6.4 10.2 10.2 0 0 0 9.1 13.9h.5zM452.8 13.4c-6.2 0-11.7 3.3-14.8 8.2a18.5 18.5 0 0 0 3.6 24.3 10.4 10.4 0 0 0 13 .6c2.2-1.5 3.8-3.7 4.5-6.1v7.8c0 2.8-.8 5-2.2 6.3-1.5 1.5-4 2.2-7.5 2.2l-6-.3c-.3 0-.7.2-.8.5l-1.6 5.5c-.1.4.1.8.5 1h.1c2.8.4 5.5.6 7 .6 6.3 0 11-1.4 14-4.1 2.7-2.5 4.2-6.3 4.5-11.4V14.2c0-.5-.4-.8-.8-.8h-13.5zm6.3 8.2v18.3a9.6 9.6 0 0 1-5.6 2h-1a10.3 10.3 0 0 1-8.8-14c1.4-3.7 5-6.3 9-6.3h6.4zM291 31.5A32 32 0 0 1 322.8 0h30.8c.6 0 1.2.5 1.2 1.2v61.5c0 1.1-1.3 1.7-2.2 1l-19.2-17a18 18 0 0 1-11 3.4 18.1 18.1 0 1 1 18.2-14.8c-.1.4-.5.7-.9.6-.1 0-.3 0-.4-.2l-3.8-3.4c-.4-.3-.6-.8-.7-1.4a12 12 0 1 0-2.4 8.3c.4-.4 1-.5 1.6-.2l14.7 13.1v-46H323a26 26 0 1 0 10 49.7c.8-.4 1.6-.2 2.3.3l3 2.7c.3.2.3.7 0 1l-.2.2a32 32 0 0 1-47.2-28.6z"></path></svg>
    </div>
    `;
};

// Render the footer with a link to the search page
const renderFooter = (query, html, customText) => {
  return html`
      <div class="aa-PanelFooter">
        <a href="/search/?q=${encodeURIComponent(query)}" class="aa-FooterSeeAll" tabindex="8">
          ${customText || 'See all results'}
        </a>
        <!-- ${renderAlgoliaLogo(html)} -->
      </div>
    `;
};

// Normalize and capitalize the search result titles
function normalizeAndCapitalize(input) {
  return input
    .split(/[\s-]+/)
    .map(word => {
      // Normalize some values here until we can do this during indexing
      if (word.toLowerCase() === 'iot') {
        return 'IoT';
      } else if (word.toLowerCase() === 'vpg') {
        return 'VPG';
      } else if (word.toLowerCase() === 'api') {
        return 'API';
      } else if (word.toLowerCase() === 'faq') {
        return word;
      } else if (word.toLowerCase() === 'for') {
        return 'for';
      } else if (word.toLowerCase() === 'soracom' && word === 'soracom') {
        return 'Soracom';
      } else if (word.toLowerCase() === 'soracom') {
        return word;
      } else if (word.toLowerCase() === 'aws') {
        return 'AWS';
      } else if (word.toLowerCase() === 'gcp') {
        return 'GCP';
      }

      // Capitalize the first letter and append the rest of the word
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// Helper function to determine if a URL is external
function isExternalLink(url) {
  try {
    const linkUrl = new URL(url, window.location.href);
    return linkUrl.origin !== window.location.origin;
  } catch (e) {
    // If URL constructor fails, assume it's a relative/internal link
    return false;
  }
}

// Helper function to check if a URL is from our site domain (developers.soracom.io)
// This helps distinguish internal links (even with production URLs) from truly external links
function isOurSiteDomain(url) {
  try {
    const linkUrl = new URL(url, window.location.href);
    // Check if the domain is developers.soracom.io (our site)
    // This works even when permalink has production URL but we're in dev
    return linkUrl.hostname === 'developers.soracom.io';
  } catch (e) {
    return false;
  }
}

// Debounce input to prevent too many requests
function debounced(fn, time) {
  let timer;
  let resolveList = [];

  return function (...args) {
    if (timer) {
      clearTimeout(timer);
    }

    return new Promise((resolve) => {
      resolveList.push(resolve);
      timer = setTimeout(() => {
        const result = fn(...args);
        resolveList.forEach((r) => r(result));
        resolveList = [];
      }, time);
    });
  };
}

const debounceMs = 200;

function initializeAutocompleteNow() {
  const { autocomplete, getAlgoliaResults } = window['@algolia/autocomplete-js'];
  const getSearchItems = debounced(({ query }) => {
    return getAlgoliaResults({
      searchClient: algoliaClient,
      queries: [
        {
          indexName,
          query,
          params: {
            attributesToSnippet: ['chunkContent:20'],
            hitsPerPage: 10,
          },
        },
      ],
    });
  }, debounceMs);

  // Hoist shared references so global listeners can access them
  let inputField;
  let autocompleteInstance;

  autocompleteInstance = autocomplete({
    container: "#autocomplete",
    placeholder: "Search",
    renderNoResults({ state, render, html }, root) {
      const query = state.query;
      if (query.trim() === '') {
        // Don't show "No results" message for empty queries
        render(
          html`<!-- No footer rendered -->`,
          root
        );
      } else {
        render(
          html`
              <div class="aa-SourceNoResults">No results found for "${query}".</div>
              ${renderFooter('', html, 'Try our search page')}
            `,
          root
        );
      }
    },
    getSources() {
      return [
        {
          sourceId: 'algoliaSearchResults',
          getItems: getSearchItems,
          getItemUrl({ item }) {
            // First check if permalink is from an external domain (not our site)
            // For truly external links, always use full permalink URL
            if (!isOurSiteDomain(item.permalink)) {
              return item.permalink;
            }
            // For our site's links (even if permalink has production URL), prefer relPermalink
            // This ensures internal links work correctly in both dev (localhost) and production
            // The relPermalink is relative to the current origin, so it works everywhere
            if (item.relPermalink && item.relPermalink.startsWith('/')) {
              return item.relPermalink;
            }
            // Fallback to permalink if relPermalink is missing
            return item.permalink;
          },
          templates: {
            item({ item, components, html }) {
              let sectionOrCategory = item.section || item.category; // favor section over category

              if (sectionOrCategory.toLowerCase() === 'soracom') {
                sectionOrCategory = item.pageTitle;
              }

              // First check if permalink is from an external domain (not our site)
              // For truly external links, always use full permalink URL
              let itemUrl;
              if (!isOurSiteDomain(item.permalink)) {
                itemUrl = item.permalink;
              } else if (item.relPermalink && item.relPermalink.startsWith('/')) {
                // For our site's links, prefer relPermalink (works in dev and production)
                itemUrl = item.relPermalink;
              } else {
                // Fallback to permalink if relPermalink is missing
                itemUrl = item.permalink;
              }
              const external = isExternalLink(itemUrl);
              const icon = item.icon || 'documents';
              const iconPath = resolveSearchIconPath(icon);

              return html`
                  <div class="aa-ItemContent">
                    <a class="aa-ItemLink" href="${itemUrl}"
                      ${external ? 'target="_blank" rel="noopener noreferrer"' : ''}>
                      <div class="aa-ItemHeader">
                        <img
                          class="aa-ItemIconLeft"
                          src="${iconPath}"
                          alt="${icon} icon"
                          data-icon="${icon}"
                        />
                        <div class="aa-ItemContentTitle">
                          ${html`<span class="item-breadcrumb">${normalizeAndCapitalize(sectionOrCategory)}</span>
                            <span class="breadcrumb-separator"> / </span>
                            ${item.chunkTitle}`}
                        </div>
                      </div>
                      <div class="aa-ItemContentBody">
                        ${components.Snippet({ hit: item, attribute: 'chunkContent' })}
                      </div>
                    </a>
                  </div>
                `;
            },
          },
        },
      ];
    },
    render({ children, state, render, html }, root) {
      render(
        html`
            ${children}
            ${renderFooter(state.query, html)}
          `,
        root
      );
    },
    detachedMediaQuery: '(max-width: 680px)',
    onStateChange({ state }) {
      inputField = document.querySelector('.aa-Input');
      // Add name attribute for accessibility and autofill support
      if (inputField && !inputField.hasAttribute('name')) {
        inputField.setAttribute('name', 'search');
      }
      alignPanel();

      const detachedContainer = document.querySelector('.aa-DetachedContainer');
      if (!detachedContainer) return;

      if (state.query.trim() !== '') {
        detachedContainer.style.setProperty('bottom', '0', 'important');
      } else {
        detachedContainer.style.setProperty('bottom', 'unset', 'important');
      }

      if (state.isOpen && inputField) {
        inputField.focus();
      }
    },
    onSubmit({ state }) {
      const query = state.query.trim();
      if (query !== '') {
        window.location.href = `/search?q=${encodeURIComponent(query)}`;
      }
    },
    onKeyDown({ event, state }) {
      if (event.key === 'Tab') {
        // Handle tabbing behavior
        const activeElement = document.activeElement;
        // TODO: Implement custom tabbing behavior here using activeElement

      }
      else if (event.key === 'Escape') {
        // Handle Escape key
        autocompleteInstance.setIsOpen(false);
      }
      else if (event.key === 'Enter') {
        // Handle Enter key
        const activeItemId = state.activeItemId;
        if (activeItemId !== null) {
          const item = state.collections[0].items.find(
            (item) => item.__autocomplete_id === activeItemId
          );
          if (item) {
            // Use the same URL logic as getItemUrl: check if external domain first
            let url;
            if (!isOurSiteDomain(item.permalink)) {
              url = item.permalink;
            } else if (item.relPermalink && item.relPermalink.startsWith('/')) {
              url = item.relPermalink;
            } else {
              url = item.permalink;
            }
            const isExternal = isExternalLink(url);
            // Open the result in a new tab if it's an external link
            if (isExternal) {
              window.open(url, '_blank');
            } else {
              window.location.href = url;
            }
            event.preventDefault();
          }
        }
      }

      // Hide the panel when the input is empty
      const detachedContainer = document.querySelector('.aa-DetachedContainer');
      if (inputField) {
        if (state.query.trim() !== '') {
          detachedContainer.style.setProperty('bottom', '0', 'important');
        } else {
          detachedContainer.style.setProperty('bottom', 'unset', 'important');
        }
      }

      alignPanel();
    },
    onDetachedMount(props) {
      const searchBar = document.querySelector('.aa-DetachedContainer');
      const panel = document.querySelector('.aa-Panel');
      const overlay = document.querySelector('.aa-DetachedOverlay');

      // Focus the input in detached mode
      const mobileInput = searchBar.querySelector('.aa-Input');
      inputField = mobileInput;
      // Add name attribute for accessibility and autofill support
      if (inputField && !inputField.hasAttribute('name')) {
        inputField.setAttribute('name', 'search');
      }

      // If there is text in the input, keep it focused
      if (inputField.value.trim() !== '') {
        inputField.focus();
      }

      // Show overlay
      overlay.style.display = 'block';

      // Function to update panel and overlay visibility
      function updatePanelAndOverlayVisibility() {
        const hasResults = props.state.collections.some(
          (collection) => collection.items.length > 0
        );
        const hasQuery = props.state.query.trim() !== '';

        panel.style.display = hasResults && hasQuery ? 'block' : 'none';
        overlay.style.display = hasResults && hasQuery ? 'none' : 'block';

        // Adjust the bottom style of the detached container when there are no results
        if (!hasQuery) {
          searchBar.style.bottom = 'unset';
        } else {
          searchBar.style.bottom = '0'; // Reset to default
        }

        // Re-align panel if it's open
        alignPanel();
      }

      // Initial update
      updatePanelAndOverlayVisibility();

      // Subscribe to state changes
      props.subscribe(({ state }) => {
        updatePanelAndOverlayVisibility();
      });

      // Detached search button (mobile search icon)
      const detachedButton = document.querySelector('.aa-DetachedSearchButton');
      if (detachedButton) {
        detachedButton.addEventListener('click', (event) => {
          const state = autocompleteInstance.getState();

          if (state.isOpen) {
            autocompleteInstance.setIsOpen(false);
            overlay.style.display = 'none';
            panel.style.display = 'none';
            document.activeElement.blur();

            // Adjust the bottom style when closed
            searchBar.style.bottom = 'unset';
          } else {
            autocompleteInstance.setIsOpen(true);
            inputField.focus();
          }

          event.stopPropagation();
        });
      }
    },
    onDetachedUnmount(props) {
      // Update inputField reference to the desktop input
      inputField = document.querySelector('#autocomplete .aa-Input');

      // If there is text in the input, keep it focused
      if (inputField && inputField.value.trim() !== '') {
        inputField.focus();
      }
    },
  });

  // Helper function to align the panel
  function alignPanel() {
    const autocompleteElement = document.querySelector('#autocomplete');
    const panelElement = document.querySelector('.aa-Panel');

    if (autocompleteElement && panelElement) {
      const autocompleteRect = autocompleteElement.getBoundingClientRect();

      // Adjust the panel's left and top positions to align with the input field
      panelElement.style.left = `${autocompleteRect.left}px`;
      panelElement.style.top = `${autocompleteRect.bottom}px`;
      panelElement.style.width = `${autocompleteRect.width}px`;
    }
  }

  // Update inputField reference
  inputField = document.querySelector('.aa-Input');

  // Add name attribute for accessibility and autofill support
  if (inputField && !inputField.hasAttribute('name')) {
    inputField.setAttribute('name', 'search');
  }

  // If there is text in the input, keep it focused
  if (inputField && inputField.value.trim() !== '') {
    inputField.focus();
  }

  // Pressing Escape key closes the autocomplete
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const detachedContainer = document.querySelector('.aa-DetachedContainer');
      const panel = document.querySelector('.aa-Panel');
      const overlay = document.querySelector('.aa-DetachedOverlay');

      if (detachedContainer && panel) {
        detachedContainer.style.display = 'none';
        panel.style.display = 'none';
        overlay.style.display = 'none';

        // Adjust the bottom style when closed
        detachedContainer.style.bottom = 'unset';
      }

      autocompleteInstance.setIsOpen(false);
    }
  });

  // CTRL + K focuses the autocomplete search input field
  document.addEventListener('keydown', (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      event.stopPropagation();
      autocompleteInstance.setIsOpen(true);

      // Focus the input field within the detached container if it exists, otherwise focus the main input
      const detachedInput = document.querySelector('.aa-DetachedContainer .aa-Input');
      (detachedInput || inputField).focus();
    }
  });

  // Desktop search icon click handler
  const searchIconButton = document.querySelector('.aa-SubmitButton');
  if (searchIconButton) {
    searchIconButton.addEventListener('click', (event) => {
      event.preventDefault();
      const query = inputField.value.trim();
      if (query !== '') {
        window.location.href = `/search?q=${encodeURIComponent(query)}`;
      }
    });
  }
}

// Initialize autocomplete when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  var alreadyInitialized = false;
  var manifestReady = false;

  function initializeIfReady() {
    if (alreadyInitialized || !manifestReady) return;
    // Check if both required libraries are available
    if (window['@algolia/autocomplete-js'] && window.algoliasearch) {
      try {
        initializeAutocompleteNow();
        alreadyInitialized = true;
      } catch(e) {
        console.error('Failed to initialize autocomplete:', e);
      }
    }
  }

  function setManifestReady() {
    manifestReady = true;
    initializeIfReady();
  }

  loadAssetManifest().then(setManifestReady).catch(setManifestReady);

  // Try to initialize immediately if libraries are already loaded
  initializeIfReady();

  // Also try after a short delay in case scripts are still loading
  setTimeout(initializeIfReady, 100);

  // Fallback: lazy load if libraries aren't available yet
  function ensureVendorsAndInit() {
    if (alreadyInitialized) return;
    function onReady() {
      initializeIfReady();
    }
    if (window['@algolia/autocomplete-js'] && window.algoliasearch) {
      onReady();
      return;
    }
    // Dynamically load libraries if not available
    var base = (window.THEME_JS_BASE || '/assets/js').replace(/\/$/, '');
    var client = document.createElement('script');
    client.src = base + '/vendor/algolia-algoliasearch-lite-4.24.0.umd.js';
    client.defer = true;
    var ac = document.createElement('script');
    ac.src = base + '/vendor/algolia-autocomplete-js-1.17.4.min.js';
    ac.defer = true;
    ac.onload = onReady;
    document.body.appendChild(client);
    document.body.appendChild(ac);
  }

  // Bind to common triggers as fallback
  var input = document.querySelector('#autocomplete input, #autocomplete .aa-Input');
  if (input) {
    input.addEventListener('focus', ensureVendorsAndInit, { once: true });
    input.addEventListener('keydown', ensureVendorsAndInit, { once: true });
  }
  var openBtn = document.querySelector('.aa-DetachedSearchButton, .aa-SubmitButton');
  if (openBtn) openBtn.addEventListener('click', ensureVendorsAndInit, { once: true });
});


//const algoliaIndex = algoliaClient.initIndex('prod_developers_search');

//const predefinedItems = [
//    {
//        label: "FAQs",
//        url: "https://support.soracom.io/hc/en-us"
//    },
//    {
//        label: "GitHub",
//        url: "https://github.com/algolia/autocomplete"
//    },
//];

//async function searchAlgoliaItems(query) {
//    if (!query) {
//        return [];
//    }
//    const searchParams = {
//        //attributesToSnippet: ['body_safe:20'], // Fetch a snippet of the first 20 words of body_safe
//        //snippetEllipsisText: '…', // Text to append to snippets indicating they are truncated
//        highlightPreTag: '<mark>',
//        highlightPostTag: '</mark>',
//    };
//    const { hits } = await algoliaIndex.search(query, searchParams);
//    return hits;
//}

//const autocompletePlugin = {
//
//};

//{
//    sourceId: "predefinedItemsPlugin",
//    getItems({ query }) {
//        if (!query || query.length < 2) {
//            return predefinedItems;
//        }
//        return predefinedItems.filter(item =>
//            item.label.toLowerCase().includes(query.toLowerCase())
//        );
//    },
//    getItemUrl({ item }) {
//        return item.url;
//    },
//    templates: {
//        header({ items, html }) {
//            return html`
//            <span class="aa-SourceHeaderTitle">Predefined Links</span>
//            <div class="aa-SourceHeaderLine" />
//            `;
//        },
//        item({ item, html }) {
//            return html`
//            <a class="aa-ItemLink" href="${item.url}">
//                <div class="aa-ItemContent">
//                    <div class="aa-ItemContentTitle">${item.label}</div>
//                </div>
//            </a>
//            `;
//        }
//    }
//},
